/**
 * お問合せフォーム（#contact-inquiry-form）のクライアント側バリデーション
 * document-request-form-validation.js と同様の visit__fieldError / is-invalid で表示する
 */
import { isValidPhoneTel, PHONE_TEL_FORMAT_MESSAGE } from './phone-validation.js';
import { isValidEmail } from './email-validation.js';
const ZIP_RE = /^\d{3}-?\d{4}$/;

function trimValue(id) {
	const el = document.getElementById(id);
	return el ? el.value.trim() : '';
}

function clearErrorForControl(form, controlId) {
	form.querySelectorAll(`.visit__fieldError[data-for="${controlId}"]`).forEach((n) => n.remove());
	const control = document.getElementById(controlId);
	if (!control) return;
	control.removeAttribute('aria-invalid');
	control.classList.remove('is-invalid');
	const wrap = control.closest('.visit__inputWrap--date');
	if (wrap) wrap.classList.remove('is-invalid');
	const schedule = control.closest('.visit__schedule');
	if (schedule) schedule.classList.remove('is-invalid');
	const consent = control.closest('.visit__consent');
	if (consent) consent.classList.remove('is-invalid');
}

export function clearContactInquiryFormErrors(form) {
	form.querySelectorAll('.visit__fieldError').forEach((n) => n.remove());
	form.querySelectorAll('.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__inputWrap--date.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__schedule.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__consent.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('[aria-invalid="true"]').forEach((n) => n.removeAttribute('aria-invalid'));
}

function mountErrorParagraph(controlId, message) {
	const p = document.createElement('p');
	p.className = 'visit__fieldError';
	p.setAttribute('data-for', controlId);
	p.setAttribute('role', 'alert');
	p.textContent = message;
	return p;
}

function showFieldError(form, controlId, message) {
	const control = document.getElementById(controlId);
	if (!control) return;

	clearErrorForControl(form, controlId);

	control.setAttribute('aria-invalid', 'true');
	control.classList.add('is-invalid');

	const wrap = control.closest('.visit__inputWrap--date');
	if (wrap) {
		wrap.classList.add('is-invalid');
		wrap.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
		return;
	}

	const schedule = control.closest('.visit__schedule');
	if (schedule) {
		schedule.classList.add('is-invalid');
		schedule.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
		return;
	}

	if (control.id === 'ciq-name-family' || control.id === 'ciq-name-given') {
		const grid = control.closest('.visit__fieldGrid--name');
		if (grid) {
			grid.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
			return;
		}
	}

	control.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
}

function focusFirst(controlId) {
	const el = document.getElementById(controlId);
	if (!el) return;
	if (el instanceof HTMLFieldSetElement) {
		const first = el.querySelector('input[type="checkbox"]');
		if (first instanceof HTMLElement) {
			first.focus();
		}
		return;
	}
	const schedule = el.closest('.visit__schedule');
	if (schedule instanceof HTMLElement) {
		const focusable = schedule.querySelector('.visit__scheduleArrow, .visit__scheduleSlot');
		if (focusable instanceof HTMLElement) {
			focusable.focus();
			return;
		}
	}
	const fpRoot = el.closest('.flatpickr');
	if (fpRoot) {
		const alt = fpRoot.querySelector('.flatpickr-alt-input');
		(alt || el).focus();
		return;
	}
	el.focus();
}

/**
 * @returns {boolean}
 */
export function validateContactInquiryForm(form) {
	clearContactInquiryFormErrors(form);

	const errors = [];

	const family = trimValue('ciq-name-family');
	const given = trimValue('ciq-name-given');
	if (!family && !given) {
		errors.push(['ciq-name-family', '性・名を入力してください。']);
	} else if (!family) {
		errors.push(['ciq-name-family', '性を入力してください。']);
	} else if (!given) {
		errors.push(['ciq-name-given', '名を入力してください。']);
	}

	const telRaw = trimValue('ciq-tel');
	if (!telRaw) {
		errors.push(['ciq-tel', '電話番号を入力してください。']);
	} else if (!isValidPhoneTel(telRaw)) {
		errors.push(['ciq-tel', PHONE_TEL_FORMAT_MESSAGE]);
	}

	const email = trimValue('ciq-email');
	if (!email) {
		errors.push(['ciq-email', 'メールアドレスを入力してください。']);
	} else if (!isValidEmail(email)) {
		errors.push(['ciq-email', 'メールアドレスの形式が正しくありません。']);
	}

	const zipRaw = trimValue('ciq-zip');
	const zipClean = zipRaw.replace(/[〒\s]/g, '');
	const addressOptional = form.hasAttribute('data-contact-inquiry-staff-flow');

	if (addressOptional) {
		if (zipRaw && !ZIP_RE.test(zipClean)) {
			errors.push(['ciq-zip', '郵便番号は7桁の半角数字で入力してください（ハイフン可）。']);
		}
	} else {
		if (!zipRaw) {
			errors.push(['ciq-zip', '郵便番号を入力してください。']);
		} else if (!ZIP_RE.test(zipClean)) {
			errors.push(['ciq-zip', '郵便番号は7桁の半角数字で入力してください（ハイフン可）。']);
		}

		const pref = trimValue('ciq-pref');
		if (!pref) {
			errors.push(['ciq-pref', '都道府県を選択してください。']);
		}

		const city = trimValue('ciq-city');
		if (!city) {
			errors.push(['ciq-city', '市区町村を入力してください。']);
		}

		const street = trimValue('ciq-street');
		if (!street) {
			errors.push(['ciq-street', '町名・番地を入力してください。']);
		}
	}

	if (errors.length === 0) {
		return true;
	}

	for (const [id, msg] of errors) {
		showFieldError(form, id, msg);
	}
	focusFirst(errors[0][0]);
	return false;
}

export function initContactInquiryFormValidation(form) {
	if (!form || form.id !== 'contact-inquiry-form') return;

	const onEdit = (e) => {
		const t = e.target;
		if (!t || !form.contains(t)) return;
		if (t.id) {
			clearErrorForControl(form, t.id);
		}
	};

	form.addEventListener('input', onEdit);
	form.addEventListener('change', onEdit);
}
